// Адреса экранов. Шаги заказа — /orders/:orderId/<шаг>, см. orders/orderSteps.ts.
import { lazy } from "react";
import { Outlet, ScrollRestoration, type RouteObject } from "react-router";
import { useScrollToTopOnNewScreen } from "./scrollReset";
import { usePageMeta } from "../site/usePageMeta";
import { ORDER_SCREEN_LOADERS } from "./orderScreens";
import { HomeScreen } from "../screens/home/HomeScreen";
import { MiniAppHome } from "../screens/miniapp/MiniAppHome";
import { useIsMiniApp } from "../telegram/channel";
import { HowItWorksScreen } from "../screens/how/HowItWorksScreen";
import { FaqScreen } from "../screens/faq/FaqScreen";
import { ShowcaseGift } from "../screens/showcase/ShowcaseGift";
import { ShowcaseMini } from "../screens/showcase/ShowcaseMini";
import { DocPageScreen } from "../screens/docs/DocPageScreen";
import { DocsScreen } from "../screens/docs/DocsScreen";
import { NotFoundScreen } from "../ui/ProblemScreen";

// Страница образцов дизайн-системы — только в режиме разработки, в сборку не попадает.
const DevComponents = import.meta.env.DEV
  ? lazy(() => import("../dev/DevComponents").then((module) => ({ default: module.DevComponents })))
  : null;

const DEV_ROUTES: RouteObject[] =
  DevComponents === null ? [] : [{ path: "dev/components", element: <DevComponents /> }];

// В Mini App главная — короткая: развилка и нижняя кнопка, без витрины.
function HomeRoute() {
  return useIsMiniApp() ? <MiniAppHome /> : <HomeScreen />;
}

// Новый экран открывается сверху (или у якоря, как /mini#faq), «назад» возвращает туда,
// где был. Без этого следующий шаг заказа открывался там, где покупатель пролистал прошлый.
function RootLayout() {
  useScrollToTopOnNewScreen();
  usePageMeta();
  return (
    <>
      <ScrollRestoration />
      <Outlet />
    </>
  );
}

// Экраны заказа, входа, учётки и оплаты грузятся отдельными файлами — только когда покупатель
// до них дошёл: публичным страницам их код не нужен (изменение seo). Публичные экраны, корень
// и «не найдено» — обычные: ленивый маршрут среди начальных не дал бы подхватить готовую страницу.
const screenRoutes: RouteObject[] = [
  ...DEV_ROUTES,
  { index: true, element: <HomeRoute /> },
  {
    path: "new/:direction",
    lazy: async () => ({ Component: (await import("../screens/newOrder/NewOrderScreen")).NewOrderScreen }),
  },
  { path: "mini", element: <ShowcaseMini /> },
  { path: "gift", element: <ShowcaseGift /> },
  { path: "how", element: <HowItWorksScreen /> },
  { path: "faq", element: <FaqScreen /> },
  { path: "login", lazy: async () => ({ Component: (await import("../screens/login/LoginScreen")).LoginScreen }) },
  {
    path: "login/telegram",
    lazy: async () => ({
      Component: (await import("../screens/login/TelegramCallbackScreen")).TelegramCallbackScreen,
    }),
  },
  {
    path: "login/link",
    lazy: async () => ({ Component: (await import("../screens/login/SiteLinkLoginScreen")).SiteLinkLoginScreen }),
  },
  { path: "orders", lazy: async () => ({ Component: (await import("../screens/orders/OrdersScreen")).OrdersScreen }) },
  {
    path: "account/delete",
    lazy: async () => ({
      Component: (await import("../screens/account/DeleteAccountScreen")).DeleteAccountScreen,
    }),
  },
  {
    path: "orders/:orderId",
    lazy: async () => ({ Component: (await ORDER_SCREEN_LOADERS.layout()).OrderLayout }),
    children: [
      { index: true, element: null },
      { path: "photos", lazy: async () => ({ Component: (await ORDER_SCREEN_LOADERS.photos()).PhotoStep }) },
      { path: "look", lazy: async () => ({ Component: (await ORDER_SCREEN_LOADERS.look()).LookRoute }) },
      {
        path: "look/own",
        lazy: async () => ({ Component: (await ORDER_SCREEN_LOADERS.lookOwn()).OwnCharacterScreen }),
      },
      {
        path: "concept",
        lazy: async () => ({ Component: (await ORDER_SCREEN_LOADERS.concept()).ConceptRoute }),
      },
      {
        path: "purchase",
        lazy: async () => ({ Component: (await ORDER_SCREEN_LOADERS.purchase()).PurchaseScreen }),
      },
      {
        path: "checkout",
        lazy: async () => ({ Component: (await ORDER_SCREEN_LOADERS.checkout()).CheckoutScreen }),
      },
      {
        path: "payment",
        lazy: async () => ({ Component: (await ORDER_SCREEN_LOADERS.payment()).PaymentScreen }),
      },
      { path: "model", lazy: async () => ({ Component: (await ORDER_SCREEN_LOADERS.model()).ModelScreen }) },
      {
        path: "status",
        lazy: async () => ({ Component: (await ORDER_SCREEN_LOADERS.status()).StatusScreen }),
      },
    ],
  },
  // После кассы для счёта из Mini App: без входа, без данных заказа (robokassa-payments).
  {
    path: "payment/return",
    lazy: async () => ({
      Component: (await import("../screens/payment/PaymentReturnScreen")).PaymentReturnScreen,
    }),
  },
  { path: "docs", element: <DocsScreen /> },
  { path: "docs/:documentId", element: <DocPageScreen /> },
  { path: "*", element: <NotFoundScreen /> },
];

export const routes: RouteObject[] = [{ element: <RootLayout />, children: screenRoutes }];
